import React, { FC, useContext } from 'react';
import { SectionHeading } from 'components/molecules';
import { DocumentsContainer, RecordsLabel } from './DueDiligenceDocuments.styles';
import { FileFormat, FileInfo, getFileExtension } from 'helpers/documentHelper';
import { ToastContext, ToastContextProps } from 'toast';
import { ToastType } from 'components/molecules/toast/types';
import DocumentCard from 'components/molecules/document-card';
import ThreeDotsContextMenu from 'components/organisms/three-dots-context-menu';
import { CbiSectionHeader } from 'pages/third-party-profile/sections/documents/Documents.styles';
import { useDownloadState } from 'hooks';

interface DocumentsProps {
  documents: FileInfo[];
}

const DueDiligenceDocuments: FC<DocumentsProps> = ({ documents }) => {
  const { openToast } = useContext<ToastContextProps>(ToastContext);
  const {
    downloads,
    resetDownload,
    failDowload,
    progressDownload,
    startDownload,
    successDownload,
    INITIALDOWNLOADSTATE,
  } = useDownloadState();

  const filesInfo = documents.filter((x) => x.isCbiFile === false) ?? [];
  const cbifilesInfo = documents.filter((x) => x.isCbiFile === true) ?? [];

  const handleFailView = (status) => {
    switch (status) {
      case 401:
        openToast({
          type: ToastType.Error,
          message: `You don't have enough permissions to view documents`,
        });
        break;

      default:
        openToast({
          type: ToastType.Error,
          message: 'An error occurred while downloading the document',
        });
        break;
    }
  };

  const handleFailDownload = (status) => {
    switch (status) {
      case 401:
        openToast({
          type: ToastType.Error,
          message: `You don't have enough permissions to download documents`,
        });
        break;

      default:
        openToast({
          type: ToastType.Error,
          message: 'An error occurred while downloading the document',
        });
        break;
    }
  };

  return (
    <section style={{ margin: 0 }}>
      <SectionHeading text={`Report attachments`} border></SectionHeading>
      <>
        <RecordsLabel>{documents.length} records</RecordsLabel>
        <DocumentsContainer>
          {filesInfo.map((fileInfo) => (
            <DocumentCard
              key={fileInfo.id}
              fileType={getFileExtension(fileInfo.fileDownloadName).toLowerCase() as FileFormat}
              date={fileInfo.uploadedDate}
              expiryDate={fileInfo.expiryDate}
              category={fileInfo.category}
              extendedNames={true}
              name={fileInfo.name}
              downloadState={downloads[fileInfo.id] ?? INITIALDOWNLOADSTATE}
              onAutoReset={() => resetDownload(fileInfo.id)}
              actionComponent={
                <ThreeDotsContextMenu
                  thirdPartyId={fileInfo.thirdPartyId}
                  fileInfo={fileInfo}
                  onFailView={handleFailView}
                  onFailDownload={handleFailDownload}
                  showDeleteOption={false}
                  onStartDownloadFile={startDownload}
                  onProgressDownloadFile={progressDownload}
                  onFailDowloadFile={failDowload}
                  onSuccessDownloadFile={successDownload}
                />
              }
            />
          ))}
        </DocumentsContainer>
        {cbifilesInfo.length > 0 && (
          <>
            <CbiSectionHeader tag="h3" value="Interview video and transcript" />
            <DocumentsContainer>
              {cbifilesInfo.map((fileInfo) => (
                <DocumentCard
                  fileSize={fileInfo.size}
                  key={fileInfo.id}
                  fileType={
                    getFileExtension(fileInfo.fileDownloadName)?.toLowerCase() as FileFormat
                  }
                  date={fileInfo.uploadedDate}
                  expiryDate={fileInfo.expiryDate}
                  category={fileInfo.category}
                  extendedNames={false}
                  name={fileInfo.name}
                  downloadState={downloads[fileInfo.id] ?? INITIALDOWNLOADSTATE}
                  onAutoReset={() => resetDownload(fileInfo.id)}
                  actionComponent={
                    <ThreeDotsContextMenu
                      thirdPartyId={fileInfo.thirdPartyId}
                      fileInfo={fileInfo}
                      onFailView={handleFailView}
                      onFailDownload={handleFailDownload}
                      showDeleteOption={false}
                      onStartDownloadFile={startDownload}
                      onProgressDownloadFile={progressDownload}
                      onFailDowloadFile={failDowload}
                      onSuccessDownloadFile={successDownload}
                    />
                  }
                />
              ))}
            </DocumentsContainer>
          </>
        )}
      </>
    </section>
  );
};

export default DueDiligenceDocuments;
